import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { DepressionScreeningFormValues } from "./interface";
import useGetDepressionScreening from "hooks/api/client/useGetDepressionScreening";
import useUpsertDepressionScreening from "hooks/api/client/useUpsertDepressionScreening";

const EMPTY_VALUES: DepressionScreeningFormValues = {
  feelingDown: "",
  littleInterest: "",
};

export const DepressionScreening: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { depressionScreening, refetchDepressionScreening } = useGetDepressionScreening(clientIdNum);
  const { upsertDepressionScreeningMutation, isUpsertDepressionScreeningLoading } = useUpsertDepressionScreening({
    onSuccess: () => {
      refetchDepressionScreening();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<DepressionScreeningFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<DepressionScreeningFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (depressionScreening) {
      const mapped: DepressionScreeningFormValues = {
        feelingDown: depressionScreening.feelingDownDepressedHopeless || depressionScreening.feeling_down_depressed_hopeless || "",
        littleInterest: depressionScreening.littleInterestOrPleasure || depressionScreening.little_interest_or_pleasure || "",
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [depressionScreening]);

  const handleChange =
    (field: keyof DepressionScreeningFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;

    const payload = {
      feelingDownDepressedHopeless: formValues.feelingDown === "" ? null : formValues.feelingDown,
      littleInterestOrPleasure: formValues.littleInterest === "" ? null : formValues.littleInterest,
    };

    upsertDepressionScreeningMutation({ clientId: clientIdNum, payload });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues) || isUpsertDepressionScreeningLoading;

  return (
    <SectionWrapper
      title="Depression Screening"
      description="Initial screening indicators used to identify potential signs of depression and inform whether further clinical evaluation or support may be warranted."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default DepressionScreening;
